import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRightIcon, BarChart3Icon, FileTextIcon, GlobeIcon, ShieldCheckIcon, ScaleIcon, ScanLineIcon, LayoutDashboardIcon, FolderIcon, SettingsIcon, CheckCircle2Icon, ClockIcon, FactoryIcon, CpuIcon, DownloadIcon, FlameIcon, RadioTowerIcon, LandmarkIcon, PackageIcon, HardHatIcon, WheatIcon, PillIcon, PlayIcon, TrendingDownIcon, ZapIcon, DatabaseIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { HowCarbonSafeWorks } from '../components/HowCarbonSafeWorks';
/* ═══════════════════════════════════════════════
   HERO IMAGE COMPOSITION
   Layered uploaded images with depth
   ═══════════════════════════════════════════════ */
function HeroImageComposition() {
  return <div className="relative w-full min-h-[480px] lg:min-h-[520px]" data-id="element-1473">
      {/* Main: Corporate team photo */}
      <motion.div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl" initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.2,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-1474">
        <img src="https://cdn.magicpatterns.com/uploads/vPSz64uEgGxaBHCJ55t71W/68820e57-4d9e-40a6-b7b3-9ac66568bb2d.png" alt="Corporate team reviewing carbon data" className="w-full h-auto rounded-2xl" data-id="element-1475" />
      </motion.div>

      {/* Overlapping: Net Zero Tracker card */}
      <motion.div className="absolute -bottom-8 -left-6 z-20 w-[55%] max-w-[280px]" initial={{
      opacity: 0,
      y: 40,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} transition={{
      duration: 0.8,
      delay: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-1476">
        <img src="https://cdn.magicpatterns.com/uploads/vGvZgfE3rh3n61m6k9oqo4/pasted-image.png" alt="Net Zero Tracker showing Scope 1, 2, 3 emissions" className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100" data-id="element-1477" />
      </motion.div>

      {/* Overlapping: Emissions Dashboard card */}
      <motion.div className="absolute -top-6 -right-6 z-20 w-[50%] max-w-[260px]" initial={{
      opacity: 0,
      y: -20,
      x: 20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} transition={{
      duration: 0.8,
      delay: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-1478">
        <img src="https://cdn.magicpatterns.com/uploads/gaWAHq8BtGuZeHvUC7M2Yu/pasted-image.png" alt="Emissions Dashboard with scope breakdown" className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100" data-id="element-1479" />
      </motion.div>
    </div>;
}
/* ═══════════════════════════════════════════════
   ANIMATED STAT
   ═══════════════════════════════════════════════ */
function AnimatedStat({
  value,
  label,
  detail
}: {
  value: string;
  label: string;
  detail: string;
}) {
  const numericMatch = value.match(/^([\d.]+)(.*)$/);
  const numericValue = numericMatch ? parseFloat(numericMatch[1]) : 0;
  const suffix = numericMatch ? numericMatch[2] : value;
  const isNumeric = !!numericMatch;
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, v => {
    if (numericValue < 10) return Number(v.toFixed(1));
    return Math.round(v);
  });
  useEffect(() => {
    if (isInView && isNumeric) {
      const controls = animate(mv, numericValue, {
        duration: 2,
        ease: [0.22, 1, 0.36, 1]
      });
      return controls.stop;
    }
  }, [isInView, numericValue, mv, isNumeric]);
  useEffect(() => {
    const unsub = rounded.on('change', v => setDisplay(v));
    return unsub;
  }, [rounded]);
  return <div ref={ref} className="text-center" data-id="element-1480">
      <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-1481">
        {isNumeric ? `${display.toLocaleString()}${suffix}` : value}
      </p>
      <p className="mt-2 text-base font-bold text-white" data-id="element-1482">{label}</p>
      <p className="mt-1 text-sm text-white/50 leading-relaxed" data-id="element-1483">{detail}</p>
    </div>;
}
/* ═══════════════════════════════════════════════
   SCOPE BREAKDOWN CHART
   ═══════════════════════════════════════════════ */
function ScopeBreakdownChart() {
  return <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-lg" data-id="element-1484">
      <div className="mb-6" data-id="element-1485">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-1486">
          Typical Enterprise Profile
        </p>
        <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-1487">
          GHG Emissions by Scope
        </p>
      </div>

      <div className="space-y-5" data-id="element-1488">
        <div data-id="element-1489">
          <div className="flex items-center justify-between mb-2" data-id="element-1490">
            <div className="flex items-center gap-2" data-id="element-1491">
              <div className="w-3 h-3 rounded-sm bg-carbon-deep" data-id="element-1492" />
              <span className="text-sm font-bold text-carbon-deep" data-id="element-1493">
                Scope 1
              </span>
            </div>
            <span className="text-sm font-extrabold text-carbon-deep" data-id="element-1494">18%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-1495">
            <motion.div className="h-full rounded-full bg-carbon-deep" initial={{
            width: 0
          }} whileInView={{
            width: '18%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            delay: 0.3,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1496" />
          </div>
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-1497">
            Direct emissions from owned facilities
          </p>
        </div>

        <div data-id="element-1498">
          <div className="flex items-center justify-between mb-2" data-id="element-1499">
            <div className="flex items-center gap-2" data-id="element-1500">
              <div className="w-3 h-3 rounded-sm bg-carbon-mid" data-id="element-1501" />
              <span className="text-sm font-bold text-carbon-deep" data-id="element-1502">
                Scope 2
              </span>
            </div>
            <span className="text-sm font-extrabold text-carbon-deep" data-id="element-1503">32%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-1504">
            <motion.div className="h-full rounded-full bg-carbon-mid" initial={{
            width: 0
          }} whileInView={{
            width: '32%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            delay: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1505" />
          </div>
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-1506">
            Indirect emissions from purchased energy
          </p>
        </div>

        <div data-id="element-1507">
          <div className="flex items-center justify-between mb-2" data-id="element-1508">
            <div className="flex items-center gap-2" data-id="element-1509">
              <div className="w-3 h-3 rounded-sm bg-carbon-bright" data-id="element-1510" />
              <span className="text-sm font-bold text-carbon-deep" data-id="element-1511">
                Scope 3
              </span>
            </div>
            <span className="text-sm font-extrabold text-carbon-deep" data-id="element-1512">50%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-1513">
            <motion.div className="h-full rounded-full bg-carbon-bright" initial={{
            width: 0
          }} whileInView={{
            width: '50%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            delay: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1514" />
          </div>
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-1515">
            Value chain emissions (upstream & downstream)
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 p-3 rounded-xl bg-carbon-bright/5 border border-carbon-bright/15" data-id="element-1516">
        <div className="w-8 h-8 rounded-lg bg-carbon-bright/15 flex items-center justify-center flex-shrink-0" data-id="element-1517">
          <BarChart3Icon className="w-4 h-4 text-carbon-bright" data-id="element-1518" />
        </div>
        <p className="text-xs text-gray-500 leading-snug" data-id="element-1519">
          <span className="font-bold text-carbon-deep" data-id="element-1520">
            Scope 3 typically represents 50-80% of total emissions
          </span>
          . CarbonSafe automates calculations across all 15 GHG Protocol
          categories.
        </p>
      </div>
    </div>;
}
/* ═══════════════════════════════════════════════
   INDUSTRY CARD
   ═══════════════════════════════════════════════ */
interface IndustryInfo {
  icon: ComponentType<{
    className?: string;
  }>;
  name: string;
  color: string;
  description: string;
}
function IndustryCard({
  industry,
  index,
  isInView
}: {
  industry: IndustryInfo;
  index: number;
  isInView: boolean;
}) {
  const IconComp = industry.icon;
  return <motion.article className="relative p-5 rounded-2xl border border-gray-100 bg-white overflow-hidden" initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: 0.08 + index * 0.05,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-1521">
      {/* Watermark background icon */}
      <div className="absolute -bottom-4 -right-4 opacity-[0.04] pointer-events-none select-none" data-id="element-1522">
        <IconComp className="w-28 h-28" data-id="element-1523" />
      </div>

      <div className="relative z-10 flex items-start gap-3.5" data-id="element-1524">
        <div className="w-11 h-11 rounded-xl bg-carbon-bright/15 text-carbon-bright flex items-center justify-center flex-shrink-0" data-id="element-1525">
          <IconComp className="w-5 h-5" data-id="element-1526" />
        </div>
        <div className="min-w-0 flex-1" data-id="element-1527">
          <h3 className="text-sm font-bold text-carbon-deep" data-id="element-1528">
            {industry.name}
          </h3>
          <p className="text-xs text-gray-500 leading-relaxed mt-1.5" data-id="element-1529">
            {industry.description}
          </p>
        </div>
      </div>
    </motion.article>;
}
/* ═══════════════════════════════════════════════
   DASHBOARD MOCKUP (Platform Preview)
   ═══════════════════════════════════════════════ */
function GHGDashboardMockup() {
  const barHeights = [82, 75, 68, 72, 60, 55, 48];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return <div className="rounded-2xl shadow-2xl border border-gray-200 overflow-hidden bg-white" style={{
    transform: 'perspective(1200px) rotateX(2deg) rotateY(-1deg) scale(0.92)'
  }} data-id="element-1530">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200" data-id="element-1531">
        <div className="flex gap-1.5" data-id="element-1532">
          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-1533" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-1534" />
          <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-1535" />
        </div>
        <div className="flex-1 mx-4" data-id="element-1536">
          <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-400 font-mono border border-gray-200 max-w-md" data-id="element-1537">
            app.carbonsafe.io/ghg-reporting
          </div>
        </div>
      </div>

      <div className="flex min-h-[420px]" data-id="element-1538">
        {/* Sidebar */}
        <div className="w-48 bg-carbon-deep flex-shrink-0 p-3 hidden sm:block" data-id="element-1539">
          <div className="flex items-center gap-2 px-2 py-2 mb-4" data-id="element-1540">
            <div className="w-7 h-7 rounded-lg bg-carbon-bright flex items-center justify-center" data-id="element-1541">
              <span className="text-white text-xs font-bold" data-id="element-1542">CS</span>
            </div>
            <span className="text-white text-sm font-bold" data-id="element-1543">CarbonSafe</span>
          </div>
          <nav className="space-y-0.5" data-id="element-1544">
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-white/10 text-white text-xs font-medium" data-id="element-1545">
              <LayoutDashboardIcon className="w-3.5 h-3.5" data-id="element-1546" /> Dashboard
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-1547">
              <BarChart3Icon className="w-3.5 h-3.5" data-id="element-1548" /> Emissions
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-1549">
              <FolderIcon className="w-3.5 h-3.5" data-id="element-1550" /> Facilities
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-1551">
              <FileTextIcon className="w-3.5 h-3.5" data-id="element-1552" /> Reports
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-1553">
              <GlobeIcon className="w-3.5 h-3.5" data-id="element-1554" /> CBAM
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-1555">
              <SettingsIcon className="w-3.5 h-3.5" data-id="element-1556" /> Settings
            </div>
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 p-4 sm:p-5 bg-gray-50 overflow-hidden" data-id="element-1557">
          <div className="flex items-center justify-between mb-4" data-id="element-1558">
            <div data-id="element-1559">
              <h3 className="text-sm font-bold text-carbon-deep" data-id="element-1560">
                GHG Inventory | FY 2026
              </h3>
              <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-1561">
                Last updated 8 min ago
              </p>
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-id="element-1562">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1563" /> CSRD
              Compliant
            </span>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4" data-id="element-1564">
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1565">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-1566">
                Total Emissions
              </p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-1567">
                12,847{' '}
                <span className="text-[10px] font-medium text-gray-400" data-id="element-1568">
                  tCO₂e
                </span>
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1569">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-1570">Scope 1</p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-1571">2,312</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1572">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-1573">Scope 2</p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-1574">4,180</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1575">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-1576">Scope 3</p>
              <p className="text-lg font-bold text-carbon-bright mt-0.5" data-id="element-1577">
                6,355
              </p>
            </div>
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 mb-4" data-id="element-1578">
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1579">
              <div className="flex items-center justify-between mb-3" data-id="element-1580">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-1581">
                  Monthly Emissions Trend
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-1582">This week</p>
              </div>
              <div className="flex items-end gap-2 h-24" data-id="element-1583">
                {barHeights.map((h, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-1584">
                    <div className="w-full rounded-t-sm bg-carbon-bright/80" style={{
                  height: `${h}%`
                }} data-id="element-1585" />
                    <span className="text-[9px] text-gray-400" data-id="element-1586">{days[i]}</span>
                  </div>)}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1587">
              <div className="flex items-center justify-between mb-3" data-id="element-1588">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-1589">
                  By Scope
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-1590">FY 2026</p>
              </div>
              <div className="space-y-2.5" data-id="element-1591">
                <div data-id="element-1592">
                  <div className="flex justify-between text-[10px] mb-1" data-id="element-1593">
                    <span className="text-gray-500 font-medium" data-id="element-1594">
                      Scope 1 — Direct
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-1595">18%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1596">
                    <div className="h-full bg-carbon-deep rounded-full" style={{
                    width: '18%'
                  }} data-id="element-1597" />
                  </div>
                </div>
                <div data-id="element-1598">
                  <div className="flex justify-between text-[10px] mb-1" data-id="element-1599">
                    <span className="text-gray-500 font-medium" data-id="element-1600">
                      Scope 2 — Energy
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-1601">32%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1602">
                    <div className="h-full bg-carbon-mid rounded-full" style={{
                    width: '32%'
                  }} data-id="element-1603" />
                  </div>
                </div>
                <div data-id="element-1604">
                  <div className="flex justify-between text-[10px] mb-1" data-id="element-1605">
                    <span className="text-gray-500 font-medium" data-id="element-1606">
                      Scope 3 — Value Chain
                    </span>
                    <span className="font-bold text-carbon-deep" data-id="element-1607">50%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1608">
                    <div className="h-full bg-carbon-bright rounded-full" style={{
                    width: '50%'
                  }} data-id="element-1609" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity feed */}
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden" data-id="element-1610">
            <div className="px-3 py-2 border-b border-gray-100" data-id="element-1611">
              <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-1612">
                Recent Activity
              </p>
            </div>
            <div className="divide-y divide-gray-50" data-id="element-1613">
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-1614">
                <FileTextIcon className="w-3 h-3 text-carbon-bright flex-shrink-0" data-id="element-1615" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-1616">
                  CSRD Report Q4 generated
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-1617">
                  ESRS E1 Climate
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-1618">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-1619" /> 15m ago
                </span>
              </div>
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-1620">
                <CheckCircle2Icon className="w-3 h-3 text-emerald-500 flex-shrink-0" data-id="element-1621" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-1622">
                  Scope 2 data imported
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-1623">
                  UMEME utility bills
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-1624">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-1625" /> 1h ago
                </span>
              </div>
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-1626">
                <ShieldCheckIcon className="w-3 h-3 text-blue-500 flex-shrink-0" data-id="element-1627" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-1628">
                  CBAM declaration submitted
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-1629">
                  Cement exports Q3
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-1630">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-1631" /> 3h ago
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
/* ═══════════════════════════════════════════════
   ICON MAP FOR FEATURES
   ═══════════════════════════════════════════════ */
const iconMap: Record<string, ComponentType<{
  className?: string;
}>> = {
  BarChart3Icon,
  FileTextIcon,
  GlobeIcon,
  ShieldCheckIcon,
  ScaleIcon,
  ScanLineIcon
};
/* ═══════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════ */
const industries: IndustryInfo[] = [{
  icon: FactoryIcon,
  name: 'Manufacturing',
  color: 'bg-slate-600',
  description: 'Cement, beverages, and industrial production. Track Scope 1 emissions from kilns, boilers, and process heat — plus Scope 2 from grid electricity and backup generators.'
}, {
  icon: FlameIcon,
  name: 'Oil & Gas',
  color: 'bg-red-600',
  description: 'Upstream exploration, pipeline operations, and refining. Environmental and Social Impact Assessments, flaring reports, and fugitive emissions tracking.'
}, {
  icon: RadioTowerIcon,
  name: 'Telecommunications',
  color: 'bg-yellow-600',
  description: 'Base stations, data centers, and network infrastructure. Growing Scope 2 emissions from grid electricity and diesel backup generators across thousands of sites.'
}, {
  icon: LandmarkIcon,
  name: 'Financial Services',
  color: 'bg-blue-600',
  description: 'Banks, insurers, and investment firms. Financed emissions (Scope 3, Category 15) from lending and investment portfolios under IFRS S2 and TCFD frameworks.'
}, {
  icon: PackageIcon,
  name: 'FMCG & Consumer Goods',
  color: 'bg-orange-600',
  description: 'Soaps, edible oils, plastics, and packaged goods. Multi-facility manufacturing with complex supply chains requiring full value chain emissions mapping.'
}, {
  icon: HardHatIcon,
  name: 'Construction & Steel',
  color: 'bg-amber-700',
  description: 'Steel processing, roofing, and building materials. Energy-intensive metal fabrication with growing carbon accounting obligations and CBAM exposure for exports.'
}, {
  icon: WheatIcon,
  name: 'Agriculture & Agro-Processing',
  color: 'bg-green-700',
  description: 'Sugar, coffee, tea, and food processing. Biomass burning, land-use change, and cogeneration reporting required under the Climate Change Act.'
}, {
  icon: PillIcon,
  name: 'Pharmaceuticals & Healthcare',
  color: 'bg-purple-600',
  description: 'Drug manufacturing and cold chain logistics. Chemical processing, refrigerant emissions, and energy-intensive cleanroom operations require environmental compliance.'
}];
const features = [{
  icon: 'BarChart3Icon',
  title: 'GHG Protocol Scopes 1-3',
  description: 'Complete greenhouse gas inventory following the GHG Protocol Corporate Standard. Automated Scope 3 calculations across 15 categories with Uganda-specific emission factors.'
}, {
  icon: 'FileTextIcon',
  title: 'IFRS S1 & S2 (ISSB)',
  description: 'Generate sustainability and climate disclosures aligned with IFRS S1 (General) and S2 (Climate). Scenario analysis, transition plans, and physical risk assessments included.'
}, {
  icon: 'GlobeIcon',
  title: 'EU CSRD Compliance',
  description: 'Double materiality assessments, ESRS-aligned reporting, and value chain due diligence. Ready for the European Sustainability Reporting Standards.'
}, {
  icon: 'ScaleIcon',
  title: 'CBAM Reporting',
  description: 'Calculate embedded emissions for goods exported to the EU. Automated CBAM declarations with product-level carbon intensity tracking.'
}, {
  icon: 'ShieldCheckIcon',
  title: 'Uganda Climate Change Act',
  description: 'Comply with the National Climate Change Act 2021 requirements for GHG inventories, climate action plans, and reporting to the Ministry of Water and Environment.'
}, {
  icon: 'ScanLineIcon',
  title: 'Audit-Ready Documentation',
  description: 'Full digital audit trail for NEMA environmental audits. Automated evidence collection, data lineage tracking, and third-party verification support.'
}];
const stats = [{
  value: '$2.4B+',
  label: 'EU-Uganda Trade',
  detail: 'Annual bilateral trade value subject to CBAM and EUDR requirements'
}, {
  value: '150+',
  label: 'Reporting Entities',
  detail: 'Ugandan companies expected to fall under mandatory ESG disclosure by 2027'
}, {
  value: '$47/ton',
  label: 'EU Carbon Price',
  detail: 'CBAM will apply EU carbon pricing to imports from non-pricing jurisdictions'
}, {
  value: '2025',
  label: 'CSRD Deadline',
  detail: 'First wave of EU sustainability reporting standards now in effect'
}];
/* ═══════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════════ */
export function GHGAccounting() {
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, {
    once: true,
    margin: '-60px'
  });
  const impactRef = useRef(null);
  const impactInView = useInView(impactRef, {
    once: true,
    margin: '-80px'
  });
  const industriesRef = useRef(null);
  const industriesInView = useInView(industriesRef, {
    once: true,
    margin: '-60px'
  });
  const featuresRef = useRef(null);
  const featuresInView = useInView(featuresRef, {
    once: true,
    margin: '-80px'
  });
  const platformRef = useRef(null);
  const platformInView = useInView(platformRef, {
    once: true,
    margin: '-60px'
  });
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, {
    once: true,
    margin: '-60px'
  });
  return <>
      {/* ═══ HERO ═══ */}
      <section className="relative w-full overflow-hidden bg-[#FAFDF7]" data-id="element-1632">
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-1633">
          <defs data-id="element-1634">
            <pattern id="ghg-hero-grid" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-1635">
              <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-1636" />
              <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-1637" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ghg-hero-grid)" data-id="element-1638" />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36" data-id="element-1639">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1640">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1641">
              <p className="text-sm font-semibold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-1642">
                GHG Accounting & ESG Reporting | Uganda
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-1643">
                Automate Your{' '}
                <span className="relative inline-block" data-id="element-1644">
                  <span className="relative z-10" data-id="element-1645">GHG & ESG Reporting</span>
                  <span className="absolute bottom-1 left-0 right-0 h-[40%] bg-carbon-bright/25 -z-0 rounded-sm" aria-hidden="true" data-id="element-1646" />
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-500 leading-relaxed max-w-xl" data-id="element-1647">
                Uganda's largest enterprises face mounting pressure from IFRS
                Sustainability Standards, EU CSRD, and CBAM — alongside domestic
                climate legislation. CarbonSafe automates Scope 1, 2, and 3
                emissions tracking, ESG reporting, and regulatory compliance in
                one platform.
              </p>
              <div className="mt-5 flex flex-wrap gap-3" data-id="element-1648">
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1649">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1650" />
                  IFRS S1/S2
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1651">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1652" />
                  CSRD Ready
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1653">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1654" />
                  CBAM Compliant
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1655">
                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1656" />
                  GHG Protocol
                </span>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-4" data-id="element-1657">
                <a href="mailto:hello@carbonsafe.earth" className="inline-flex items-center gap-2 px-7 py-3.5 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-1658">
                  See CarbonSafe in action
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1659" />
                </a>
                <a href="mailto:hello@carbonsafe.earth" className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-carbon-bright text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-1660">
                  Explore The Platform
                </a>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative" data-id="element-1661">
              <HeroImageComposition data-id="element-1662" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section ref={statsRef} className="w-full bg-carbon-deep py-20 sm:py-28" data-id="element-1663">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1664">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={statsInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1665">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" data-id="element-1666">
              The ESG compliance imperative
            </h2>
            <p className="mt-4 text-lg text-white/50 max-w-2xl mx-auto" data-id="element-1667">
              Global sustainability regulations are reshaping how Ugandan
              enterprises measure, report, and reduce their environmental
              impact.
            </p>
          </motion.div>
          <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8" initial="hidden" animate={statsInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.12
            }
          }
        }} data-id="element-1668">
            {stats.map(stat => <motion.div key={stat.label} variants={{
            hidden: {
              opacity: 0,
              y: 30
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6
              }
            }
          }} data-id="element-1669">
                <AnimatedStat {...stat} data-id="element-1670" />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <HowCarbonSafeWorks verticalName="GHG accounting" subtext="From data collection to audit-ready reports, four steps to verified emissions management." steps={[{
      icon: 'BarChart3Icon',
      title: 'Data Collection',
      description: 'Automated ingestion from utility bills, fuel records, supply chain data, and IoT sensors across all operations.'
    }, {
      icon: 'CpuIcon',
      title: 'Emissions Calculation',
      description: 'GHG Protocol-aligned calculations for Scope 1, 2, and 3 using Uganda-specific and IPCC emission factors.'
    }, {
      icon: 'FileTextIcon',
      title: 'Report Generation',
      description: 'Auto-generate IFRS S1/S2, CSRD, CBAM, and GHG Protocol reports with full audit trail.'
    }, {
      icon: 'ShieldCheckIcon',
      title: 'Verification & Filing',
      description: 'Third-party verification support and direct submission to regulatory bodies and ESG rating agencies.'
    }]} data-id="element-1671" />

      {/* ═══ SCOPE BREAKDOWN ═══ */}
      <section ref={impactRef} className="w-full bg-white py-24 sm:py-32" data-id="element-1672">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1673">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center" data-id="element-1674">
            <motion.div initial={{
            opacity: 0,
            x: -40
          }} animate={impactInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1675">
              <img src="https://cdn.magicpatterns.com/uploads/w43D4ABhU3TKEzXySfM9pN/pasted-image.png" alt="Emissions over time by department" className="w-full h-auto rounded-2xl shadow-lg border border-gray-100" data-id="element-1676" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 40
          }} animate={impactInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.7,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1677">
              <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-6" data-id="element-1678">
                Full-Scope Coverage
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-carbon-deep tracking-tight leading-tight" data-id="element-1679">
                From direct operations to entire value chains
              </h2>
              <p className="mt-6 text-base text-gray-500 leading-relaxed" data-id="element-1680">
                Most Ugandan enterprises only track Scope 1 and 2 emissions —
                missing the 50-80% of their carbon footprint hidden in supply
                chains, logistics, and product use. CarbonSafe's automated Scope
                3 engine maps your entire value chain, from raw material
                extraction to end-of-life disposal.
              </p>
              <div className="mt-10 grid grid-cols-3 gap-6" data-id="element-1681">
                <div data-id="element-1682">
                  <p className="text-3xl sm:text-4xl font-extrabold text-carbon-bright" data-id="element-1683">
                    15
                  </p>
                  <p className="mt-1 text-sm text-gray-500 font-medium" data-id="element-1684">
                    Scope 3 categories
                  </p>
                </div>
                <div data-id="element-1685">
                  <p className="text-3xl sm:text-4xl font-extrabold text-carbon-bright" data-id="element-1686">
                    500+
                  </p>
                  <p className="mt-1 text-sm text-gray-500 font-medium" data-id="element-1687">
                    Data sources
                  </p>
                </div>
                <div data-id="element-1688">
                  <p className="text-3xl sm:text-4xl font-extrabold text-carbon-bright" data-id="element-1689">
                    99.9%
                  </p>
                  <p className="mt-1 text-sm text-gray-500 font-medium" data-id="element-1690">
                    Calculation accuracy
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ INDUSTRIES WE SERVE ═══ */}
      <section ref={industriesRef} className="w-full bg-gray-50 py-24 sm:py-32" data-id="element-1691">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1692">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={industriesInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1693">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-6" data-id="element-1694">
              Who We Help
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-1695">
              Industries <span className="text-carbon-bright" data-id="element-1696">we serve</span>
            </h2>
            <p className="mt-5 text-lg text-gray-500 max-w-3xl mx-auto" data-id="element-1697">
              CarbonSafe provides tailored GHG accounting and ESG compliance
              solutions for Uganda's most carbon-intensive and
              regulation-exposed sectors.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-1698">
            {industries.map((industry, i) => <IndustryCard key={industry.name} industry={industry} index={i} isInView={industriesInView} data-id="element-1699" />)}
          </div>

          {/* Regulatory Callout */}
          <motion.div className="mt-14" initial={{
          opacity: 0,
          y: 20
        }} animate={industriesInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.6,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1700">
            <div className="p-8 rounded-2xl bg-carbon-deep text-white" data-id="element-1701">
              <div className="flex items-start gap-5" data-id="element-1702">
                <div className="w-12 h-12 rounded-xl bg-carbon-bright/20 flex items-center justify-center flex-shrink-0" data-id="element-1703">
                  <ShieldCheckIcon className="w-6 h-6 text-carbon-bright" data-id="element-1704" />
                </div>
                <div className="flex-1" data-id="element-1705">
                  <h3 className="text-lg font-bold" data-id="element-1706">
                    Uganda's Climate Regulatory Landscape
                  </h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed" data-id="element-1707">
                    The National Climate Change Act 2021 mandates GHG
                    inventories for designated entities. NEMA's Environmental
                    Audit Regulations require annual environmental audits for
                    manufacturing facilities. The Uganda Green Growth
                    Development Strategy sets economy-wide emission reduction
                    targets. A carbon tax framework is under active discussion
                    at the Uganda Revenue Authority, expected to affect
                    industrial emitters by 2027.
                  </p>
                  <a href="mailto:hello@carbonsafe.earth" className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-carbon-bright hover:text-carbon-light transition-colors" data-id="element-1708">
                    <DownloadIcon className="w-4 h-4" data-id="element-1709" />
                    Download Compliance Guide
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section ref={featuresRef} className="w-full bg-white py-24 sm:py-32" data-id="element-1710">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1711">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={featuresInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1712">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-1713">
              Comprehensive ESG reporting for{' '}
              <span className="text-carbon-bright" data-id="element-1714">every framework</span>
            </h2>
            <p className="mt-5 text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-1715">
              One platform that speaks the language of every major
              sustainability reporting standard and Ugandan regulation.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate={featuresInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1716">
            {features.map(feature => {
            const IconComponent = iconMap[feature.icon];
            return <motion.article key={feature.title} className="p-8 rounded-2xl border border-gray-100 bg-gray-50" variants={{
              hidden: {
                opacity: 0,
                y: 40
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1]
                }
              }
            }} data-id="element-1717">
                  <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-1718">
                    {IconComponent && <IconComponent className="w-7 h-7 text-carbon-bright" data-id="element-1719" />}
                  </div>
                  <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-1720">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed" data-id="element-1721">
                    {feature.description}
                  </p>
                </motion.article>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ═══ PLATFORM PREVIEW ═══ */}
      <section ref={platformRef} className="w-full bg-gray-50 py-24 sm:py-32" data-id="element-1722">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1723">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={platformInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1724">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-6" data-id="element-1725">
              Platform Preview
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-1726">
              GHG accounting and ESG reporting in{' '}
              <span className="text-carbon-bright" data-id="element-1727">one dashboard</span>
            </h2>
            <p className="mt-5 text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-1728">
              From emissions data collection to CSRD-compliant reports: measure,
              manage, and disclose with confidence.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 60
        }} animate={platformInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1729">
            <img src="https://cdn.magicpatterns.com/uploads/gaWAHq8BtGuZeHvUC7M2Yu/pasted-image.png" alt="Emissions Dashboard with scope and category breakdown" className="w-full h-auto rounded-2xl shadow-lg border border-gray-100" data-id="element-1730" />
            <img src="https://cdn.magicpatterns.com/uploads/vGvZgfE3rh3n61m6k9oqo4/pasted-image.png" alt="Net Zero Tracker with scope emissions and compliance status" className="w-full h-auto rounded-2xl shadow-lg border border-gray-100" data-id="element-1731" />
          </motion.div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section ref={ctaRef} className="w-full py-24 sm:py-32 bg-white" data-id="element-1732">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1733">
          <motion.div className="relative overflow-hidden rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center" style={{
          background: 'linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)'
        }} initial={{
          opacity: 0,
          y: 40
        }} animate={ctaInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1734">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{
            background: 'radial-gradient(circle, #52B788, transparent)'
          }} aria-hidden="true" data-id="element-1735" />
            <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10" style={{
            background: 'radial-gradient(circle, #6BCB77, transparent)'
          }} aria-hidden="true" data-id="element-1736" />
            <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-1737">
              Every ton measured.
              <br data-id="element-1738" />
              <span className="text-carbon-bright" data-id="element-1739">Every report trusted.</span>
            </h2>
            <p className="relative mt-6 text-lg text-white/60 max-w-xl mx-auto" data-id="element-1740">
              Join Uganda's leading enterprises using CarbonSafe to automate GHG
              accounting and meet global ESG reporting standards.
            </p>
            <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1741">
              <a href="mailto:hello@carbonsafe.earth" className="inline-flex items-center gap-2.5 px-8 py-4 bg-carbon-bright text-white text-base font-semibold rounded-full hover:bg-carbon-light transition-colors shadow-lg shadow-black/20" data-id="element-1742">
                Start Your ESG Journey
                <ArrowRightIcon className="w-4 h-4" data-id="element-1743" />
              </a>
              <a href="mailto:hello@carbonsafe.earth" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-full hover:bg-white/10 transition-colors" data-id="element-1744">
                Download Case Study
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1745" />
    </>;
}